import { setDisplayErrorSnackBar } from "../../../../features/userSlice";
import { optifundDashboardService } from "../../../../services/api/optifund-dashboard.endpoints";
import store from "../../../../store";

// Returns whether the query actually succeeded (setData was called) - callers
// that need to distinguish "loading" from "failed" (see useOptifundTabData's
// setError param) rely on this instead of just the loading flag, since a
// failed call must stop looking like it's still loading.
async function runQuery(
  oauthTokenHeader: string,
  queryId: string,
  websiteSharedUid: string,
  setData: any,
  startDate?: string,
  endDate?: string,
  brand?: string,
  month?: number,
  year?: number,
): Promise<boolean> {
  try {
    if (!websiteSharedUid) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Missing website_shared_uid - cannot run this query.",
          type: "error",
        }),
      );
      return false;
    }

    const response = await optifundDashboardService.runOptifundBizLayerQuery(
      oauthTokenHeader,
      queryId,
      websiteSharedUid,
      startDate,
      endDate,
      brand,
      month,
      year,
    );
    if (!response || response.status !== 200) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Something went wrong...",
          type: "error",
        }),
      );
      return false;
    }

    const rows = response?.data?.data?.query_output;
    if (!Array.isArray(rows)) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Unexpected response format from the query.",
          type: "error",
        }),
      );
      return false;
    }

    setData(rows);
    return true;
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return false;
  }
}

// Params-object variant of runQuery for the Brand Stages tab. Resolves to
// the query_output rows, or null if the call failed (an error snackbar has
// already been shown by then) - the tab fires several of these in parallel
// and needs each result back directly rather than through a setter.
async function runQueryWithParams(
  oauthTokenHeader: string,
  queryId: string,
  queryParams: Record<string, string>,
): Promise<any[] | null> {
  try {
    const response = await optifundDashboardService.runOptifundBizLayerQueryWithParams(
      oauthTokenHeader,
      queryId,
      queryParams,
    );
    if (!response || response.status !== 200) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Something went wrong...",
          type: "error",
        }),
      );
      return null;
    }

    const rows = response?.data?.data?.query_output;
    if (!Array.isArray(rows)) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Unexpected response format from the query.",
          type: "error",
        }),
      );
      return null;
    }

    return rows;
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return null;
  }
}

export const OptifundDashboardEndPointLogic = { runQuery, runQueryWithParams };
